/**
 * Journal d'événements append-only (SPECIFICATION.md §8.1).
 *
 * Chaque événement est immuable et chaîné à celui qui le précède **dans la
 * même instance émettrice**. La fusion de deux journaux est donc une simple
 * union, sans conflit possible : c'est ce qui permet à l'enseignant de
 * reprendre la main après une panne sans perdre un seul vote.
 */

import { fingerprint } from './hash.js';
import type { ElectionState } from './state.js';
import type { EventType, JournalEvent } from './types.js';

export interface MakeEventParams {
  readonly electionId: string;
  readonly type: EventType;
  readonly payload: unknown;
  /** Instance émettrice : `T_xxx` pour le bureau, `I_xxx` pour un isoloir. */
  readonly origin: string;
  /** Journal déjà projeté, pour reprendre compteur et empreinte précédente. */
  readonly state: ElectionState | null;
  readonly ts?: number;
}

/** Construit un événement chaîné, prêt à être ajouté au journal. */
export function makeEvent(params: MakeEventParams): JournalEvent {
  const counter = (params.state?.lastCounter[params.origin] ?? 0) + 1;
  const prevHash = params.state?.lastHash[params.origin] ?? '';
  const ts = params.ts ?? Date.now();

  const body = {
    electionId: params.electionId,
    type: params.type,
    payload: params.payload,
    ts,
    origin: params.origin,
    counter,
    prevHash,
  };

  return { ...body, id: fingerprint(body), hash: fingerprint(body) };
}

/** Empreinte de l'événement, recalculée depuis son contenu. */
export function eventHash(event: JournalEvent): string {
  return fingerprint({
    electionId: event.electionId,
    type: event.type,
    payload: event.payload,
    ts: event.ts,
    origin: event.origin,
    counter: event.counter,
    prevHash: event.prevHash,
  });
}

export interface ChainCheck {
  readonly ok: boolean;
  readonly origin: string | null;
  readonly reason: string | null;
}

/**
 * Vérifie la continuité des compteurs et de la chaîne d'empreintes,
 * instance par instance. Un journal altéré est détecté, jamais ignoré.
 */
export function verifyChain(events: readonly JournalEvent[]): ChainCheck {
  const parOrigine = new Map<string, JournalEvent[]>();
  for (const event of events) {
    const liste = parOrigine.get(event.origin) ?? [];
    liste.push(event);
    parOrigine.set(event.origin, liste);
  }

  for (const [origin, liste] of parOrigine) {
    const triee = [...liste].sort((a, b) => a.counter - b.counter);
    let precedent = '';
    let attendu = 1;
    for (const event of triee) {
      if (event.counter !== attendu) {
        return { ok: false, origin, reason: `compteur ${event.counter} au lieu de ${attendu}` };
      }
      if (event.prevHash !== precedent) {
        return { ok: false, origin, reason: `chaîne rompue au compteur ${event.counter}` };
      }
      if (eventHash(event) !== event.hash) {
        return { ok: false, origin, reason: `empreinte invalide au compteur ${event.counter}` };
      }
      precedent = event.hash;
      attendu += 1;
    }
  }

  return { ok: true, origin: null, reason: null };
}

/**
 * Ordre canonique de fusion : compteur croissant, puis instance.
 * À l'intérieur d'une même instance, l'ordre du compteur est l'ordre réel ;
 * entre instances, les événements sont indépendants (les transitions de phase
 * ne proviennent que du bureau).
 */
export function mergeOrder(events: readonly JournalEvent[]): JournalEvent[] {
  return [...events].sort(
    (a, b) => a.counter - b.counter || (a.origin < b.origin ? -1 : a.origin > b.origin ? 1 : 0),
  );
}
